import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import type { SearchResult, SearchResultType } from "../lib/types.js";
import { Icon, type IconName } from "../components/Icon.js";
import type { SidebarSection } from "../workspace/Sidebar.js";

const TYPE_META: Record<SearchResultType, { label: string; icon: IconName; section: SidebarSection | "manuscript" }> = {
  scene: { label: "Manuscript", icon: "file", section: "manuscript" },
  character: { label: "Characters", icon: "users", section: "characters" },
  storyBible: { label: "Story Bible", icon: "book", section: "storyBible" },
  location: { label: "Locations", icon: "map", section: "locations" },
  timeline: { label: "Timeline", icon: "timeline", section: "timeline" },
  plotThread: { label: "Plot Threads", icon: "thread", section: "plotThreads" },
  note: { label: "Notes", icon: "note", section: "notes" }
};

interface Props {
  projectId: string;
  onClose: () => void;
  onOpenScene: (sceneId: string) => void;
  onOpenSection: (section: SidebarSection, focusId: string) => void;
}

export function GlobalSearch({ projectId, onClose, onOpenScene, onOpenSection }: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const debounce = useRef<number | null>(null);

  useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(async () => {
      try {
        const res = await api.search(projectId, query.trim());
        setResults(res.results);
        setActiveIndex(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => {
      if (debounce.current) window.clearTimeout(debounce.current);
    };
  }, [query, projectId]);

  const activate = (result: SearchResult) => {
    if (result.type === "scene" && result.sceneId) {
      onOpenScene(result.sceneId);
    } else {
      const section = TYPE_META[result.type].section;
      if (section !== "manuscript") onOpenSection(section, result.id);
    }
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") onClose();
    else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && results[activeIndex]) {
      activate(results[activeIndex]);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={onClose} style={{ alignItems: "flex-start", paddingTop: "10vh" }}>
      <div className="search-panel glass" onMouseDown={(e) => e.stopPropagation()} onKeyDown={onKeyDown}>
        <div className="search-input-row">
          <Icon name="search" size={18} style={{ color: "var(--text-muted)" }} />
          <input
            className="search-input"
            placeholder="Search the whole project…"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
          />
          {loading && <Icon name="clock" size={15} className="spin" style={{ color: "var(--text-muted)" }} />}
          <kbd className="search-kbd">Esc</kbd>
        </div>
        <div className="search-results">
          {query.trim() && !loading && results.length === 0 ? (
            <div className="search-empty">No matches for “{query.trim()}”.</div>
          ) : (
            results.map((result, i) => {
              const meta = TYPE_META[result.type];
              return (
                <button
                  key={`${result.type}-${result.id}`}
                  className={`search-result ${i === activeIndex ? "active" : ""}`}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => activate(result)}
                >
                  <Icon name={meta.icon} size={15} className="search-result-icon" />
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="row" style={{ gap: 8 }}>
                      <span className="strong truncate">{result.title}</span>
                      <span className="search-badge">{result.context || meta.label}</span>
                    </div>
                    <div className="truncate faint" style={{ fontSize: 12 }}>{result.snippet}</div>
                  </div>
                </button>
              );
            })
          )}
          {!query.trim() && (
            <div className="search-hint">
              Search across manuscript, characters, story bible, locations, timeline, plot threads, and notes.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
